import { type ClientPassage } from '../dm/Passage.js';
import { createSlice, createSelector, type PayloadAction } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';
import { type RootState } from '../dm/Root.js';

interface PaginationObject {
  id: string;
}

/**
 * Just enough to look a passage up again (via `usePassageById`) - not a
 * data snapshot, so it can never go stale relative to background sync
 * writes the way holding a full `ClientPassage` here used to.
 */
export interface SelectedPassageRef {
  id: string;
  isTemp: boolean;
}

export interface PaginationPageData {
  pageName: string;
  pageOfData?: number | undefined;
  hasMoreData: boolean;
  items: PaginationObject[];
}

export interface IUISlice {
  dateShowingInActions?: string;
  selectedPassage?: SelectedPassageRef;
  dateForReadingPlan?: string;
  tempPassages: ClientPassage[];
  newVersionReload: boolean;
  prayerSearchString: string;
  filteredPrayerTags: string[];
  filteredPassageTags: string[];
  expandedPassages: string[];
  displayedHelpTexts: string[];
  paginatedData: PaginationPageData[];
  showAllHelpText: boolean;
  forceHideHelpText: boolean;
  commentItemIDs: string[];
}

const initialState: IUISlice = {
  tempPassages: [],
  newVersionReload: false,
  prayerSearchString: '',
  filteredPrayerTags: [],
  filteredPassageTags: [],
  expandedPassages: [],
  displayedHelpTexts: [],
  paginatedData: [],
  showAllHelpText: false,
  forceHideHelpText: false,
  commentItemIDs: [],
};

export const uiSlice = createSlice({
  name: 'ui3',
  initialState: initialState,
  reducers: {
    updateDateShowingInActions: (state: IUISlice, action: PayloadAction<string>) => {
      state.dateShowingInActions = action.payload;
    },
    updateSelectedPassage: (state: IUISlice, action: PayloadAction<SelectedPassageRef | undefined>) => {
      state.selectedPassage = action.payload;
    },
    updateNewVersionReload: (state: IUISlice, action: PayloadAction<boolean>) => {
      state.newVersionReload = action.payload;
    },
    updateDateForReadingPlan: (state: IUISlice, action: PayloadAction<string>) => {
      state.dateForReadingPlan = action.payload;
    },
    removeTempPassage: (state: IUISlice, action: PayloadAction<string>) => {
      const newList = state.tempPassages.slice().filter((item) => item.id !== action.payload);

      state.tempPassages = newList;
    },
    updateTempPassage: (state: IUISlice, action: PayloadAction<ClientPassage>) => {
      const list = state.tempPassages.slice();
      const index = list.findIndex((item) => item.id === action.payload.id);
      if (index < 0) return;

      list[index].osis = action.payload.osis;
      list[index].version = action.payload.version;
      list[index].hashtags = action.payload.hashtags;
      list[index].bibleViewOptions = action.payload.bibleViewOptions;

      state.tempPassages = list;
    },
    setTempPassagePinned: (state: IUISlice, action: PayloadAction<string>) => {
      const list = state.tempPassages.slice();
      for (const p of list) {
        if (p.id === action.payload) {
          p.isPinned = true;
        } else {
          p.isPinned = false;
        }
      }

      state.tempPassages = list;
    },
    addTempPassage: (state: IUISlice, action: PayloadAction<ClientPassage>) => {
      const oldList = state.tempPassages.slice();
      const newList: ClientPassage[] = [action.payload].concat(oldList);

      state.tempPassages = newList;
    },
    removeSelectedTempPassages: (state: IUISlice, action: PayloadAction<string[]>) => {
      const newList = state.tempPassages.filter((item) => !action.payload.includes(item.id));
      state.tempPassages = newList;
    },
    setPrayerSearchString: (state: IUISlice, action: PayloadAction<string>) => {
      state.prayerSearchString = action.payload;
    },
    addFilteredPrayerTag: (state: IUISlice, action: PayloadAction<string>) => {
      const newList = state.filteredPrayerTags.slice();
      newList.push(action.payload);
      state.filteredPrayerTags = newList;
    },
    deleteFilteredPrayerTag: (state: IUISlice, action: PayloadAction<string>) => {
      const newArr = state.filteredPrayerTags.filter((item) => item !== action.payload);
      state.filteredPrayerTags = newArr;
    },
    clearFilteredPrayerTags: (state: IUISlice) => {
      state.filteredPrayerTags = [];
    },
    addFilteredPassageTag: (state: IUISlice, action: PayloadAction<string>) => {
      const newList = state.filteredPassageTags.slice();
      newList.push(action.payload);
      state.filteredPassageTags = newList;
    },
    deleteFilteredPassageTag: (state: IUISlice, action: PayloadAction<string>) => {
      const newArr = state.filteredPassageTags.filter((item) => item !== action.payload);
      state.filteredPassageTags = newArr;
    },
    clearFilteredPassageTags: (state: IUISlice) => {
      state.filteredPassageTags = [];
    },
    addExpandedPassage: (state: IUISlice, action: PayloadAction<string>) => {
      state.expandedPassages.push(action.payload);
    },
    deleteExpandedPassage: (state: IUISlice, action: PayloadAction<string>) => {
      const newArr = state.expandedPassages.filter((item) => item !== action.payload);
      state.expandedPassages = newArr;
    },
    addDisplayedHT: (state: IUISlice, action: PayloadAction<{ name: string; max: number; firstLevel: boolean }>) => {
      const { name, max, firstLevel } = action.payload;
      if (state.displayedHelpTexts.includes(name)) return;

      if (firstLevel || state.displayedHelpTexts.length < max) {
        state.displayedHelpTexts.push(name);
      }
    },
    setDisplayedHTs: (state: IUISlice, action: PayloadAction<string[]>) => {
      state.displayedHelpTexts = action.payload;
    },
    deleteDisplayedHT: (state: IUISlice, action: PayloadAction<string>) => {
      const newArr = state.displayedHelpTexts.slice();
      for (let i = 0; i < newArr.length; i++) {
        /*
         * Replace it with an empty string instead of deleting it, so
         * we're not adding more help text in this session
         */
        if (newArr[i] === action.payload) newArr[i] = '';
      }
      state.displayedHelpTexts = newArr;
    },
    clearDisplayedHT: (state: IUISlice) => {
      state.displayedHelpTexts = [];
    },
    setPaginationData: (state: IUISlice, action: PayloadAction<PaginationPageData>) => {
      let newArray = state.paginatedData.slice();
      newArray = newArray.filter((item) => item.pageName !== action.payload.pageName);
      newArray.push(action.payload);
      state.paginatedData = newArray;
    },
    clearPaginationData: (state: IUISlice, action: PayloadAction<string>) => {
      let newArray = state.paginatedData.slice();
      newArray = newArray.filter((item) => item.pageName !== action.payload);
      const newPageData: PaginationPageData = {
        pageName: action.payload,
        hasMoreData: true,
        items: [],
      };
      newArray.push(newPageData);
      state.paginatedData = newArray;
    },
    clearPaginationDataStartingWith: (state: IUISlice, action: PayloadAction<string>) => {
      let newArray = state.paginatedData.slice();
      newArray = newArray.filter((item) => !item.pageName.startsWith(action.payload));
      state.paginatedData = newArray;
    },
    updateShowAllHelpText: (state: IUISlice, action: PayloadAction<boolean>) => {
      state.showAllHelpText = action.payload;
    },
    updateForceHideHelpText: (state: IUISlice, action: PayloadAction<boolean>) => {
      state.forceHideHelpText = action.payload;
    },
    addCommentItemID: (state: IUISlice, action: PayloadAction<string>) => {
      if (state.commentItemIDs.includes(action.payload)) return;
      state.commentItemIDs = state.commentItemIDs.concat(action.payload);
    },
    deleteCommentItemID: (state: IUISlice, action: PayloadAction<string>) => {
      state.commentItemIDs = state.commentItemIDs.filter((id) => id !== action.payload);
    },
    resetUIState: (state: IUISlice) => {
      state.dateForReadingPlan = initialState.dateForReadingPlan;
      state.dateShowingInActions = initialState.dateShowingInActions;
      state.selectedPassage = initialState.selectedPassage;
      state.prayerSearchString = '';
      state.filteredPrayerTags = [];
      state.filteredPassageTags = [];
      state.expandedPassages = [];
      state.displayedHelpTexts = [];
      state.paginatedData = [];
      state.showAllHelpText = false;
      state.forceHideHelpText = false;
      state.commentItemIDs = [];
    },
  },
  selectors: {
    getSelectedPassage: (sliceState) => sliceState.selectedPassage,
    getPrayerSearchString: (sliceState) => sliceState.prayerSearchString,
    getTempPassages: (sliceState) => sliceState.tempPassages,
    getTagsForTempPassages: createSelector(
      (sliceState: IUISlice) => sliceState.tempPassages,
      (tempPassages) => Array.from(new Set(tempPassages.flatMap((p) => p.hashtags || [])))
    ),
    getNewVersionReload: (sliceState) => sliceState.newVersionReload,
    getDateShowingInActions: createSelector(
      (sliceState: IUISlice) => sliceState.dateShowingInActions,
      (dateShowingInActions) => {
        return dateShowingInActions && dateShowingInActions.length > 0
          ? dateShowingInActions
          : DateTime.now().toISODate();
      }
    ),
    getDateForReadingPlan: createSelector(
      (sliceState: IUISlice) => sliceState.dateForReadingPlan,
      (dateForReadingPlan) => {
        return dateForReadingPlan && dateForReadingPlan.length > 0 ? dateForReadingPlan : DateTime.now().toISODate();
      }
    ),
    getFilteredPrayerTags: (sliceState) => sliceState.filteredPrayerTags,
    getFilteredPassageTags: (sliceState) => sliceState.filteredPassageTags,
    getExpandedPassages: (sliceState) => sliceState.expandedPassages,
    getDisplayedHelpTexts: (sliceState) => sliceState.displayedHelpTexts,
    getPaginationData: (sliceState) => sliceState.paginatedData,
    getShowAllHelpText: (sliceState) => sliceState.showAllHelpText,
    getForceHideHelpText: (sliceState) => sliceState.forceHideHelpText,
    getCommentItemIDs: (sliceState) => sliceState.commentItemIDs,
  },
});

export const {
  updateDateShowingInActions,
  updateSelectedPassage,
  addCommentItemID,
  addDisplayedHT,
  addExpandedPassage,
  addFilteredPassageTag,
  addFilteredPrayerTag,
  addTempPassage,
  clearDisplayedHT,
  clearFilteredPassageTags,
  clearFilteredPrayerTags,
  clearPaginationData,
  clearPaginationDataStartingWith,
  deleteCommentItemID,
  deleteDisplayedHT,
  deleteExpandedPassage,
  deleteFilteredPassageTag,
  deleteFilteredPrayerTag,
  removeSelectedTempPassages,
  removeTempPassage,
  resetUIState,
  setPaginationData,
  setPrayerSearchString,
  updateDateForReadingPlan,
  updateNewVersionReload,
  updateShowAllHelpText,
  updateTempPassage,
  setTempPassagePinned,
  setDisplayedHTs,
  updateForceHideHelpText,
} = uiSlice.actions;

export const {
  getCommentItemIDs,
  getDateShowingInActions,
  getDateForReadingPlan,
  getDisplayedHelpTexts,
  getExpandedPassages,
  getFilteredPassageTags,
  getFilteredPrayerTags,
  getNewVersionReload,
  getPaginationData,
  getPrayerSearchString,
  getSelectedPassage,
  getShowAllHelpText,
  getTagsForTempPassages,
  getTempPassages,
  getForceHideHelpText,
} = uiSlice.getSelectors<RootState>((rootState) => rootState.ui);
